import type { Gql } from '@transcend-io/type-utils';
import { getEntries } from '@transcend-io/type-utils';

import { isSchema } from './typeGuards';
import type { AnySchema, SchemaToType } from './types';

/**
 * Convert a schema definition into a gql fragment
 *
 * @param schema - The schema definition to convert
 * @param includeTypeName - Whether to explicitly ask for the __typename field in the response
 * @param excludeResponseFields - The fields to exclude from the response
 * @returns The frontend gql fragment to request
 */
export function mkGql<TSchema extends AnySchema>(
  schema: TSchema,
  includeTypeName?: boolean,
  excludeResponseFields?: string[],
): Gql<SchemaToType<TSchema>> {
  return getEntries(schema.fields)
    .filter(([, { deprecated }]) => !deprecated)
    .filter(
      ([fieldName]) =>
        !excludeResponseFields || !excludeResponseFields.includes(fieldName),
    )
    .map(([fieldName, { type }]) => {
      // Determine the GraphQLType
      const nonFuncType = typeof type === 'function' ? type() : type;

      // Catch recursion errors
      if (!nonFuncType) {
        throw new Error(
          `Failed to get type for field: "${schema.name}.${fieldName}"`,
        );
      }
      return `${fieldName}${
        isSchema(nonFuncType)
          ? ` {\n ${includeTypeName ? '__typename\n' : ''} ${mkGql(
              nonFuncType,
              includeTypeName,
            )}\n }`
          : ''
      }`;
    })
    .join('\n') as Gql<SchemaToType<TSchema>>;
}
